/**
 * Mesure le recouvrement du bas du viewport par l'interface du navigateur et
 * l'expose en CSS via `--mobile-dock-lift`.
 *
 * Pourquoi une mesure plutot qu'une valeur fixe : les elements ancres en
 * `position: fixed; bottom: 0` se positionnent sur le *layout viewport*, qui
 * sur iOS inclut la zone occupee par la barre d'outils basse. Un dock pose a
 * `bottom: 0` peut donc se retrouver partiellement dessous. La hauteur de cette
 * barre varie selon le navigateur (Safari, Chrome iOS, Brave...), le modele et
 * l'orientation : toute constante en dur est fausse quelque part. C'etait le
 * cas du `13dvh` precedent, qui laissait ~86 px de vide sur iPhone 13 Pro.
 *
 * `visualViewport` donne la zone reellement visible ; la difference avec le
 * layout viewport est exactement ce recouvrement. Il vaut 0 sur desktop et sur
 * les navigateurs qui n'empietent pas, ce qui rend le mecanisme neutre par
 * defaut.
 */

// Les barres d'outils des navigateurs mobiles culminent autour d'une centaine
// de pixels. Au-dela, l'ecart vient du clavier virtuel : il faut alors garder la
// derniere valeur connue et non relever les docks, sans quoi toute l'interface
// bondirait pendant la saisie (le clavier iOS occupe ~40% de la hauteur).
const MAX_BROWSER_CHROME_PX = 160;

export function useViewportInsets() {
    let frame = 0;

    function measure() {
        const viewport = window.visualViewport;
        const root = document.documentElement;

        if (!viewport) {
            // Sans `visualViewport` (navigateurs anciens), on laisse la valeur
            // definie en CSS plutot que d'imposer une estimation.
            return;
        }

        const layoutHeight = window.innerHeight;
        const visibleBottom = viewport.height + viewport.offsetTop;
        const overlap = layoutHeight - visibleBottom;

        if (overlap > MAX_BROWSER_CHROME_PX) {
            return;
        }

        root.style.setProperty('--mobile-dock-lift', `${Math.max(0, Math.round(overlap))}px`);
    }

    function scheduleMeasure() {
        // `visualViewport` emet en rafale pendant le scroll : on se limite a une
        // mesure par frame.
        if (frame) {
            return;
        }
        frame = requestAnimationFrame(() => {
            frame = 0;
            measure();
        });
    }

    onMounted(() => {
        measure();
        window.visualViewport?.addEventListener('resize', scheduleMeasure);
        window.visualViewport?.addEventListener('scroll', scheduleMeasure);
        window.addEventListener('orientationchange', scheduleMeasure);
    });

    onBeforeUnmount(() => {
        if (frame) {
            cancelAnimationFrame(frame);
            frame = 0;
        }
        window.visualViewport?.removeEventListener('resize', scheduleMeasure);
        window.visualViewport?.removeEventListener('scroll', scheduleMeasure);
        window.removeEventListener('orientationchange', scheduleMeasure);
    });

    return { measure };
}
